import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Loader2, Plus, Check, Trash2, Flag } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import CrmTaskFormModal from './CrmTaskFormModal';

const PRIO = { high: 'text-red-400', medium: 'text-yellow-400', low: 'text-white/40' };

export default function CustomerTasksPanel({ email, customerName }) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['crm-tasks', email],
    queryFn: () => base44.entities.CustomerTask.filter({ customer_email: email }, '-due_date', 100),
    enabled: !!email,
  });

  const toggle = async (t) => {
    const done = t.status === 'completed';
    await base44.entities.CustomerTask.update(t.id, {
      status: done ? 'pending' : 'completed',
      completed_date: done ? '' : new Date().toISOString().slice(0, 10),
    });
    qc.invalidateQueries({ queryKey: ['crm-tasks', email] });
  };

  const remove = async (t) => {
    await base44.entities.CustomerTask.delete(t.id);
    qc.invalidateQueries({ queryKey: ['crm-tasks', email] });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-white/40 text-xs">{tasks.length} task(s)</p>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 border border-primary/40 hover:bg-primary/10 text-primary text-[10px] font-bold tracking-wider px-3 py-1.5 transition-colors">
          <Plus className="w-3.5 h-3.5" /> ADD TASK
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
      ) : tasks.length === 0 ? (
        <p className="text-white/30 text-xs py-8 text-center border border-white/5 bg-[#0d0d0f]">No tasks. Add a follow-up or reminder.</p>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => {
            const overdue = t.status === 'pending' && t.due_date && new Date(t.due_date) < new Date(new Date().toDateString());
            return (
              <div key={t.id} className="flex items-center gap-3 border border-white/5 bg-[#0d0d0f] px-4 py-3">
                <button onClick={() => toggle(t)} className={`w-5 h-5 border flex items-center justify-center shrink-0 ${t.status === 'completed' ? 'bg-green-500/20 border-green-500/40 text-green-400' : 'border-white/20 hover:border-white/40'}`}>
                  {t.status === 'completed' && <Check className="w-3 h-3" />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium truncate ${t.status === 'completed' ? 'text-white/30 line-through' : 'text-white'}`}>{t.title}</p>
                  {t.description && <p className="text-white/40 text-xs truncate">{t.description}</p>}
                </div>
                {t.priority && t.priority !== 'low' && <Flag className={`w-3.5 h-3.5 ${PRIO[t.priority]}`} />}
                {t.due_date && (
                  <span className={`text-[10px] font-bold tracking-wider shrink-0 ${overdue ? 'text-red-400' : 'text-white/40'}`}>
                    {format(parseISO(t.due_date), 'dd MMM')}{overdue && ' · OVERDUE'}
                  </span>
                )}
                <button onClick={() => remove(t)} className="text-white/20 hover:text-red-400 transition-colors shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <CrmTaskFormModal
          email={email}
          customerName={customerName}
          onClose={() => setShowForm(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['crm-tasks', email] })}
        />
      )}
    </div>
  );
}